import {
  exportConstructionEstimate, exportConstructionEstimateDraft,
  finalizeConstructionEstimate,
  type EstimateCapabilities, type EstimateDraft, type EstimateRevision,
} from '../api/constructionPriceEstimatorFunctions';
import { nestedObject } from './ConstructionPriceDraftState';

export class DownloadPreconditionError extends Error {}

const downloadBlob = ({ blob, filename }: { blob: Blob; filename: string }): void => {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
  } finally {
    URL.revokeObjectURL(url);
  }
};

/** A saved revision can be exported even when editing is disabled. */
export async function exportSavedConstructionEstimate(
  flowId: string, revision: number, isCurrent: () => boolean,
): Promise<boolean> {
  const exported = await exportConstructionEstimate(flowId, revision);
  if (!isCurrent()) return false;
  downloadBlob(exported);
  return true;
}

/** Export the exact persisted draft fence without saving or finalizing. */
export async function exportSavedConstructionEstimateDraft(
  flowId: string, draft: EstimateDraft, isCurrent: () => boolean,
): Promise<boolean> {
  if (!draft.content_hash) throw new DownloadPreconditionError('No saved output is available to download for this stage.');
  const exported = await exportConstructionEstimateDraft(flowId, draft.draft_revision, draft.content_hash);
  if (!isCurrent()) return false;
  downloadBlob(exported);
  return true;
}

interface DownloadInputs {
  flowId: string;
  accepted: EstimateDraft;
  revision: EstimateRevision | null;
  capabilities: EstimateCapabilities | undefined;
  romFinalizationAllowed: boolean;
  allAcknowledged: boolean;
  romAckRequired: boolean;
  romAcknowledged: boolean;
  requiredAcknowledgements: string[];
  romAcknowledgementKey: string;
  isCurrent: () => boolean;
}

/** The server rechecks revisions, hashes and permissions on every endpoint. */
export async function downloadConstructionEstimate({
  flowId, accepted, revision, capabilities, romFinalizationAllowed,
  allAcknowledged, romAckRequired, romAcknowledged,
  requiredAcknowledgements, romAcknowledgementKey, isCurrent,
}: DownloadInputs): Promise<{ revision: EstimateRevision | null; draftOnly: boolean } | null> {
  if (!accepted.content_hash) {
    throw new DownloadPreconditionError('Save the contract details before downloading an estimate.');
  }
  const matchesResult = revision?.draft_revision === accepted.draft_revision
    && (!revision?.draft_content_hash || revision.draft_content_hash === accepted.content_hash);
  let exported: { blob: Blob; filename: string };
  let finalRevision = revision;
  if (!revision || !matchesResult) {
    exported = await exportConstructionEstimateDraft(
      flowId, accepted.draft_revision, accepted.content_hash,
    );
    if (!isCurrent()) return null;
  } else {
    if (capabilities?.can_export === false || capabilities?.export_available === false) {
      throw new DownloadPreconditionError('Estimate export is unavailable under the current server policy. Your fields were saved.');
    }
    if (revision.status === 'calculated') {
      if (capabilities?.can_finalize === false || !romFinalizationAllowed) {
        throw new DownloadPreconditionError('This result cannot be finalized under the current server policy. Your fields were saved.');
      }
      if (!allAcknowledged || (romAckRequired && !romAcknowledged)) {
        throw new DownloadPreconditionError('Complete the required estimate acknowledgement before downloading. Your fields were saved.');
      }
      const finalized = await finalizeConstructionEstimate(
        flowId, revision.revision, accepted.draft_revision,
        [...requiredAcknowledgements, ...(romAckRequired ? [romAcknowledgementKey] : [])],
      );
      if (!isCurrent()) return null;
      finalRevision = nestedObject<EstimateRevision>(finalized, 'revision') ?? finalized;
    } else if (revision.status !== 'finalized') {
      throw new DownloadPreconditionError('The estimate is still calculating. Your fields were saved; try downloading when it finishes.');
    }
    exported = await exportConstructionEstimate(flowId, revision.revision);
    if (!isCurrent()) return null;
  }
  if (!isCurrent()) return null;
  downloadBlob(exported);
  return { revision: finalRevision, draftOnly: !revision || !matchesResult };
}
